import { activityLogger } from '@databases/logs/activity-logger';
import {
  type CommentThreadJSON,
  type MutableCommentThreads
} from '@databases/model/commentThreads';
import {
  type Database,
  type DatabaseJSON,
  type DatabaseViewOrderJSON,
  getOrderWithFallback,
  type MutableDatabase,
  type ViewOrderWriteBehavior
} from '@databases/model/database';
import { type EntryJSON, type MutableEntry } from '@databases/model/entry';
import { type FieldJSON, FieldType, type MutableField } from '@databases/model/field';
import { type AllValueJSON, type MutableValue } from '@databases/model/value';
import {
  type DatabaseViewJSON,
  isBuiltInCollabLayoutType,
  type MutableView
} from '@databases/model/views/view';
import * as Y from 'yjs';

import {
  BackendAgentOrigin,
  CanvasTransactionOrigin,
  originatesFromRemoteBackendAgent
} from '@canvas/transaction';

import { type DefaultValueConverters } from './default-value-converters.ts';
import { SynchronizedArray } from './helpers/synchronized-array.ts';
import { SynchronizedMap } from './helpers/synchronized-map.ts';
import { SynchronizedRecord } from './helpers/synchronized-record.ts';
import { YUniqueArray } from './helpers/y-unique-array.ts';
import { YAITextValue } from './values/y-ai-text-value.ts';
import { YView } from './views/y-view.ts';
import { YCommentThreads } from './y-comment-threads.ts';
import { YEntry } from './y-entry.ts';
import { YField } from './y-field.ts';
import { YViewOrders } from './y-view-orders.ts';
import { YViewsArray } from './y-views-array.ts';

export const Properties = {
  entries: 'e',
  entriesById: 'ebi',

  fields: 'f',
  fieldsById: 'fbi',

  settings: 's',
  commentThreads: 'ct',
  defaultEntry: 'de',

  views: 'v',
  viewsById: 'vbi',
  viewOrders: 'vo'
};

const SettingsProperties = {
  defaultViewId: 'dvi',
  isLocked: 'il'
};

export class YSchemaSettings extends SynchronizedRecord {
  constructor(data: Y.Map<unknown>) {
    super(data);
  }

  get defaultViewId(): string {
    return this.getString(SettingsProperties.defaultViewId, '');
  }

  set defaultViewId(val: string) {
    this.set(SettingsProperties.defaultViewId, val);
  }

  get isLocked(): boolean | undefined {
    return this.getBoolean(SettingsProperties.isLocked, undefined);
  }

  set isLocked(val: boolean | undefined) {
    this.set(SettingsProperties.isLocked, val);
  }

  observeDeep(cb: () => void) {
    this.data.observeDeep(cb);
  }

  unobserveDeep(cb: () => void) {
    this.data.unobserveDeep(cb);
  }

  static fromJSON(json: { isLocked: string | undefined }): YSchemaSettings {
    return new YSchemaSettings(new Y.Map([[SettingsProperties.isLocked, json.isLocked]]));
  }
}

// TODO: move me
export const setValueValue = (
  value: MutableValue<FieldType>,
  valueJson: AllValueJSON
) => {
  switch (valueJson.type) {
    case FieldType.Text: {
      const textValue = value as MutableValue<FieldType.Text>;
      if (valueJson.value.rich) {
        textValue.setContent(valueJson.value.rich);
      } else {
        textValue.insert(0, valueJson.value.plain ?? '');
      }
      break;
    }
    case FieldType.AiSummary: {
      const aiTextValue = value as MutableValue<FieldType.AiSummary>;
      if (valueJson.value.rich) {
        aiTextValue.setContent(valueJson.value.rich);
      } else {
        aiTextValue.insert(0, valueJson.value.plain ?? '');
      }

      if (value instanceof YAITextValue) {
        if (valueJson.value.metadata) {
          value.setMetadata(valueJson.value.metadata);
        } else {
          value.clearMetadata();
        }
      }
      break;
    }
    case FieldType.Date:
      (value as MutableValue<FieldType.Date>).value = valueJson.value;
      break;
    case FieldType.Number:
      (value as MutableValue<FieldType.Number>).value = valueJson.value;
      break;
    case FieldType.Checkbox:
      (value as MutableValue<FieldType.Checkbox>).value = valueJson.value;
      break;
    case FieldType.Select:
      valueJson.value.map((val) => (value as MutableValue<FieldType.Select>).add(val));
      break;
    case FieldType.User:
      valueJson.value.map((val) => (value as MutableValue<FieldType.User>).add(val));
      break;
    case FieldType.Attachment:
      valueJson.value.forEach((v) =>
        (value as MutableValue<FieldType.Attachment>).add(v)
      );
      break;
    case FieldType.Hyperlink:
      valueJson.value.forEach((v) => (value as MutableValue<FieldType.Hyperlink>).add(v));
      break;
    case FieldType.PageLink:
      valueJson.value.forEach((v) => (value as MutableValue<FieldType.PageLink>).add(v));
      break;
    case FieldType.EntryLink:
      valueJson.value.forEach((v) => (value as MutableValue<FieldType.EntryLink>).add(v));
      break;
    case FieldType.JiraIssue:
      valueJson.value.forEach((v) => (value as MutableValue<FieldType.JiraIssue>).add(v));
      break;
  }
};

export type DatabaseDiff = {
  fieldsOrderingChanged: boolean;
  entriesOrderingChanged: boolean;
  changedFieldIds: string[];
  changedEntryIds: string[];
  threadsChanged: boolean;
  viewsChanged: boolean;
  defaultEntryChanged: boolean;
  othersChanged: boolean;
};

export type ViewOrderingMode = 'global' | 'view-specific';

export interface BackendAgentUndoCaptureConfig {
  isEnabled: boolean;
  localAccountId: string;
  onEligibleBackendAgentUpdate?: VoidFunction;
}

type AgentUndoGroupState =
  | 'inactive'
  | 'awaiting-agent-transaction'
  | 'capturing-agent-transactions';

export class YDatabase implements Database, MutableDatabase {
  doc: Y.Doc;
  #entries: YUniqueArray<string>;
  #entriesById: SynchronizedMap<YEntry>;

  #fields: YUniqueArray<string>;
  #fieldsById: SynchronizedMap<YField>;

  #views: YViewsArray;
  #viewsById: SynchronizedMap<YView>;
  #viewOrders: YViewOrders;

  #commentThreads: SynchronizedMap<YCommentThreads>;
  #settings: YSchemaSettings;
  #defaultEntry: YEntry;

  #undoManager: Y.UndoManager;
  #agentUndoGroupState: AgentUndoGroupState = 'inactive';
  #backendAgentUndoCapture?: BackendAgentUndoCaptureConfig;

  constructor(
    doc: Y.Doc | undefined,
    private defaultValueConverters: DefaultValueConverters
  ) {
    doc = doc ?? new Y.Doc();
    this.doc = doc;
    this.#fields = new YUniqueArray(doc.getArray(Properties.fields));
    this.#fieldsById = new SynchronizedMap(doc.getMap(Properties.fieldsById), (input) => {
      if (!(input instanceof Y.Map)) {
        return undefined;
      }

      return new YField(input);
    });

    this.#views = new YViewsArray(doc.getArray(Properties.views));
    this.#viewsById = new SynchronizedMap(doc.getMap(Properties.viewsById), (input) => {
      if (!(input instanceof Y.Map)) {
        return undefined;
      }

      return new YView(input, this.defaultValueConverters);
    });
    this.#viewOrders = new YViewOrders(doc.getMap(Properties.viewOrders));

    this.#entries = new YUniqueArray<string>(
      new SynchronizedArray<string>(doc.getArray(Properties.entries), (el) =>
        typeof el === 'string' ? el : undefined
      )
    );

    this.#entriesById = new SynchronizedMap<YEntry>(
      doc.getMap(Properties.entriesById),
      (input) => {
        if (!(input instanceof Y.Map)) {
          return undefined;
        }

        return new YEntry(input, false, defaultValueConverters);
      }
    );

    this.#settings = new YSchemaSettings(doc.getMap(Properties.settings));
    this.#commentThreads = new SynchronizedMap<YCommentThreads>(
      doc.getMap(Properties.commentThreads),
      (input) => {
        if (!(input instanceof Y.Array)) {
          return undefined;
        }

        return new YCommentThreads(input);
      }
    );

    this.#defaultEntry = new YEntry(
      doc.getMap(Properties.defaultEntry),
      true,
      defaultValueConverters
    );

    this.#undoManager = new Y.UndoManager(
      [
        doc.getArray(Properties.fields),
        doc.getMap(Properties.fieldsById),
        doc.getArray(Properties.entries),
        doc.getMap(Properties.entriesById),
        doc.getArray(Properties.views),
        doc.getMap(Properties.viewsById),
        doc.getMap(Properties.viewOrders),
        doc.getMap(Properties.settings),
        doc.getMap(Properties.defaultEntry)
      ],
      {
        trackedOrigins: new Set([
          CanvasTransactionOrigin.LocalDefault,
          CanvasTransactionOrigin.Agent,
          BackendAgentOrigin
        ]),
        captureTimeout: Number.POSITIVE_INFINITY,
        captureTransaction: (transaction) => {
          const backendAgentUndoCapture = this.#backendAgentUndoCapture;
          const origin = transaction.origin;
          if (origin instanceof BackendAgentOrigin) {
            const isEligibleBackendAgentUpdate =
              backendAgentUndoCapture !== undefined &&
              originatesFromRemoteBackendAgent(transaction) &&
              origin.agentOwnerAccountId === backendAgentUndoCapture.localAccountId;

            if (!isEligibleBackendAgentUpdate) {
              return false;
            }

            backendAgentUndoCapture.onEligibleBackendAgentUpdate?.();
            if (!backendAgentUndoCapture.isEnabled) {
              return false;
            }

            this.#undoManager?.stopCapturing();
            return true;
          }

          const isAgentTransaction = transaction.origin === CanvasTransactionOrigin.Agent;
          const isLocalTransaction =
            transaction.origin === CanvasTransactionOrigin.LocalDefault;
          const isAgentUndoGroupActive = this.#agentUndoGroupState !== 'inactive';
          const shouldMergeWithPrevious =
            isAgentTransaction &&
            this.#agentUndoGroupState === 'capturing-agent-transactions';

          if ((isAgentTransaction || isLocalTransaction) && !shouldMergeWithPrevious) {
            this.#undoManager?.stopCapturing();
          }
          if (isAgentUndoGroupActive && (isAgentTransaction || isLocalTransaction)) {
            this.#agentUndoGroupState = isAgentTransaction
              ? 'capturing-agent-transactions'
              : 'awaiting-agent-transaction';
          }

          return !isAgentTransaction || isAgentUndoGroupActive;
        }
      }
    );

    this.#undoManager.on('stack-item-added', ({ origin }) => {
      if (origin instanceof BackendAgentOrigin) {
        this.#undoManager.stopCapturing();
      }
    });
  }

  /**
   * Configures browser-local capture for remote backend agent operations.
   */
  configureBackendAgentUndoCapture(config: BackendAgentUndoCaptureConfig): void {
    this.#backendAgentUndoCapture = config;
  }

  get undoManager(): Y.UndoManager {
    return this.#undoManager;
  }

  /**
   * Groups consecutive frontend-agent mutations into one undo item until cleanup.
   * A local user transaction starts a separate undo item.
   */
  beginAgentUndoGroup(): () => void {
    this.#undoManager.stopCapturing();
    this.#agentUndoGroupState = 'awaiting-agent-transaction';

    return () => {
      if (this.#agentUndoGroupState === 'inactive') {
        return;
      }

      this.#undoManager.stopCapturing();
      this.#agentUndoGroupState = 'inactive';
    };
  }

  get entries(): string[] {
    return this.#entries.toArray().filter((entryId) => entryId in this.#entriesById.data);
  }

  get entriesById(): Record<string, YEntry> {
    return this.#entriesById.data;
  }

  get isEmpty() {
    let empty = true;

    for (const fieldId of this.fields) {
      for (const entryId of this.entries) {
        const entryValue = this.entriesById[entryId].getValue(fieldId);

        if (!entryValue) {
          continue;
        }

        if (!entryValue.isEmpty) {
          empty = false;
          break;
        }
      }
    }

    return empty;
  }

  get commentThreads(): Record<string, YCommentThreads> {
    return this.#commentThreads.data;
  }

  set commentThreads(threadIdsMap: SynchronizedMap<YCommentThreads>) {
    this.#commentThreads = threadIdsMap;
  }

  /**
   * Adds an comment thread id for whatever value is passed in.
   * @param id ID for value with the comment thread.
   * @param threadId ID for comment thread.
   * @returns Newly created Entry
   */
  addCommentThread(id: string, thread: CommentThreadJSON): MutableCommentThreads {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      threadsArray.addThread(thread);
      return threadsArray;
    }
    const threadsArray = YCommentThreads.fromJSON([thread]);
    this.#commentThreads.set(id, threadsArray);
    return threadsArray;
  }

  setCommentThread(id: string, thread: CommentThreadJSON): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const mutableThreads = threadsArray.threads;
      const mutableThread = mutableThreads.find((t) => t.threadId === thread.threadId);

      if (mutableThread) {
        mutableThread.threadId = thread.threadId;
        mutableThread.isResolved = thread.isResolved;
      }
    }
  }

  setCommentThreadResolved(id: string, threadId: string, resolved: boolean): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const thread = threadsArray.threads.find((t) => t.threadId === threadId);
      if (thread) {
        thread.isResolved = resolved;
      }
    }
  }

  removeCommentThread(id: string, threadId: string): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const index = threadsArray.threads.map((t) => t.threadId).indexOf(threadId);
      threadsArray.deleteThread(index);
      if (threadsArray.toJSON().length === 0) {
        this.#commentThreads.delete(id);
      }
    }
  }

  removeCommentsOnValue(valueId: string): void {
    this.#commentThreads.delete(valueId);
  }

  setCommentThreadCommentCount(id: string, threadId: string, commentCount: number): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const thread = threadsArray.threads.find((t) => t.threadId === threadId);
      if (thread) {
        thread.commentCount = commentCount;
      }
    }
  }

  incrementCommentThreadCommentCount(id: string, threadId: string): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const thread = threadsArray.threads.find((t) => t.threadId === threadId);
      if (thread) {
        thread.commentCount++;
      }
    }
  }

  decrementCommentThreadCommentCount(id: string, threadId: string): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const thread = threadsArray.threads.find((t) => t.threadId === threadId);
      if (thread) {
        thread.commentCount--;
      }
    }
  }

  incrementCommentThreadVersionNumber(id: string, threadId: string): void {
    if (id in this.#commentThreads.data) {
      const threadsArray = this.#commentThreads.data[id];
      const thread = threadsArray.threads.find((t) => t.threadId === threadId);
      if (thread) {
        thread.versionNumber++;
      }
    }
  }

  /**
   * Adds a new, empty Entry with the given ID.
   * @param id ID for the new entry.
   * @returns Newly created Entry
   */
  addEntry(id: string): YEntry {
    const entry = new YEntry(new Y.Map(), false, this.defaultValueConverters);

    this.doc.transact(() => {
      this.#entriesById.set(id, entry);
      this.#entries.addEnd(id);
    });

    activityLogger.log('addEntry', { ...entry.toJSON() });

    return entry;
  }

  /**
   * Adds an entry from its JSON representation, matches addFieldFromJSON
   * @param entryId ID for the new entry.
   * @param entryJSON JSON representation of the y-Entry.
   */
  addEntryFromJSON(entryId: string, entryJSON: EntryJSON): MutableEntry {
    const entry = YEntry.fromJSON(entryJSON, false, this.defaultValueConverters);

    this.doc.transact(() => {
      this.#entriesById.set(entryId, entry);
      this.#entries.addEnd(entryId);
    });

    return entry;
  }

  /**
   * Adds a new, empty Entry with the given ID at a specific position.
   * @param id ID for the new entry.
   * @param index Index where to add the new entry.
   * @returns Newly created Entry
   */
  addEntryAt(id: string, index: number): YEntry {
    const entry = new YEntry(new Y.Map(), false, this.defaultValueConverters);

    this.doc.transact(() => {
      this.#entriesById.set(id, entry);
      this.#entries.addAt(id, index);
    });

    activityLogger.log('addEntry', { ...entry.toJSON() });

    return entry;
  }

  deleteEntry(id: string): void {
    this.deleteEntries([id]);
  }

  deleteEntries(ids: readonly string[]): void {
    this.doc.transact(() => {
      ids.forEach((id) => {
        this.#entries.delete(id);
        this.#entriesById.delete(id);
      });
      this.#viewOrders.removeEntries(ids);
    });

    ids.forEach((id) => activityLogger.log('deleteEntry', { id }));
  }

  get fieldsById(): Record<string, YField> {
    return this.#fieldsById.data;
  }

  get fields(): string[] {
    return this.#fields.toArray().filter((fieldId) => fieldId in this.#fieldsById.data);
  }

  addFieldFromJSON(fieldId: string, fieldJSON: FieldJSON): MutableField {
    const yfield = YField.fromJSON(fieldJSON);

    this.doc.transact(() => {
      this.#fieldsById.set(fieldId, yfield);
      this.#fields.addEnd(fieldId);
    });

    return yfield;
  }

  /**
   * Adds a new field with the given ID at a specific position.
   * @param fieldId ID for the new field.
   * @param fieldJSON Field json content of the new field.
   * @param index Index where to add the new field.
   * @returns Newly created Field
   */
  addFieldAt(fieldId: string, fieldJSON: FieldJSON, index: number): MutableField {
    const yfield = YField.fromJSON(fieldJSON);

    this.doc.transact(() => {
      this.#fieldsById.set(fieldId, yfield);
      this.#fields.addAt(fieldId, index);
    });

    return yfield;
  }

  get views() {
    return this.#views.toArray().filter((viewId) => viewId in this.#viewsById.data);
  }

  get viewsById(): Readonly<Record<string, MutableView>> {
    return this.#viewsById.data;
  }

  get viewOrders(): Readonly<Record<string, DatabaseViewOrderJSON>> {
    return this.#viewOrders.toJSON(this.views);
  }

  setViewFieldOrder(
    viewId: string,
    order: readonly string[],
    behavior: ViewOrderWriteBehavior = 'activate-view-specific-ordering'
  ): void {
    if (viewId in this.#viewsById.data) {
      this.#viewOrders.setFieldOrder(viewId, order, behavior);
    }
  }

  setViewEntryOrder(
    viewId: string,
    order: readonly string[],
    behavior: ViewOrderWriteBehavior = 'activate-view-specific-ordering'
  ): void {
    if (viewId in this.#viewsById.data) {
      this.#viewOrders.setEntryOrder(viewId, order, behavior);
    }
  }

  /**
   * Synchronizes the persisted ordering mode with the experiment assignment.
   * Rolling back promotes the first view into the legacy global arrays once,
   * while retaining every per-view order for a later treatment re-enable.
   */
  synchronizeViewOrderingMode(mode: ViewOrderingMode): void {
    this.mutate((db) => {
      if (mode === 'view-specific') {
        this.#viewOrders.markViewSpecificOrderingActive();
        return;
      }

      if (!this.#viewOrders.shouldPromoteToGlobal) {
        return;
      }

      const firstViewOrder = db.viewOrders[db.views[0]];
      const fieldOrder = getOrderWithFallback(db.fields, firstViewOrder?.fieldOrder);
      const entryOrder = getOrderWithFallback(db.entries, firstViewOrder?.entryOrder);

      if (fieldOrder.length > 0) {
        this.#fields.addBefore(fieldOrder, db.fields[0]);
      }
      if (entryOrder.length > 0) {
        this.#entries.addBefore(entryOrder, db.entries[0]);
      }

      this.#viewOrders.markGlobalOrderingActive();
    }, CanvasTransactionOrigin.TriggerObservers);
  }

  addView(viewId: string, view: DatabaseViewJSON): MutableView {
    const yview = YView.fromJSON(view, this.defaultValueConverters);

    this.doc.transact(() => {
      this.#views.addEnd(viewId);
      this.#viewsById.set(viewId, yview);
    });

    return yview;
  }

  setView(viewId: string, view: DatabaseViewJSON): MutableView {
    const yview = YView.fromJSON(
      this.#withPreservedExtensionLayoutType(viewId, view),
      this.defaultValueConverters
    );
    this.#viewsById.set(viewId, yview);

    return yview;
  }

  /**
   * The Redux `DatabaseView` projects unknown extension layouts to a built-in
   * type, so a write-back would otherwise clobber the real extension id that
   * only exists in collab. When the current view holds an extension layout and
   * the incoming type is a built-in, keep the existing extension id. Extension
   * layout types are changed exclusively through the SDK.
   */
  #withPreservedExtensionLayoutType(
    viewId: string,
    view: DatabaseViewJSON
  ): DatabaseViewJSON {
    const existing = this.#viewsById.data[viewId];

    if (
      existing &&
      !isBuiltInCollabLayoutType(existing.layoutType) &&
      isBuiltInCollabLayoutType(view.layoutType)
    ) {
      return { ...view, layoutType: existing.layoutType };
    }

    return view;
  }

  deleteView(viewId: string): void {
    this.doc.transact(() => {
      this.#views.delete(viewId);
      this.#viewsById.delete(viewId);
      this.#viewOrders.deleteView(viewId);
    });
  }

  get defaultViewId(): string {
    return this.#settings.defaultViewId;
  }

  set defaultViewId(val: string) {
    this.#settings.defaultViewId = val;
  }

  get isLocked(): boolean | undefined {
    return this.#settings.isLocked;
  }

  set isLocked(val: boolean | undefined) {
    this.#settings.isLocked = val;
  }

  observeSettings(callback: () => void): () => void {
    this.#settings.observeDeep(callback);

    return () => this.#settings.unobserveDeep(callback);
  }

  get defaultEntry() {
    return this.#defaultEntry;
  }

  reorderView(viewId: string, newIndex: number): void {
    if (!(viewId in this.viewsById) || !this.views.includes(viewId)) {
      return;
    }
    this.#views.addBefore([viewId], this.views[newIndex]);
  }

  reorderField(fieldId: string, newIndex: number): void {
    if (!(fieldId in this.fieldsById) || !this.fields.includes(fieldId)) {
      return;
    }
    this.#fields.addBefore([fieldId], this.#fields.toArray()[newIndex]);
  }

  reorderEntries(entryIds: string[], newIndex: number): void {
    if (
      !entryIds.every((id) => id in this.entriesById) ||
      !entryIds.every((id) => this.entries.includes(id))
    ) {
      return;
    }
    this.#entries.addBefore(entryIds, this.#entries.toArray()[newIndex]);
  }

  deleteField(fieldId: string): void {
    this.deleteFields([fieldId]);
  }

  deleteFields(fieldIds: readonly string[]): void {
    fieldIds.forEach((fieldId) => {
      this.#fields.delete(fieldId);
      this.#fieldsById.delete(fieldId);
    });
    this.#viewOrders.removeFields(fieldIds);
  }

  toJSON(): DatabaseJSON {
    const viewOrders = this.viewOrders;

    return {
      fields: this.fields,
      fieldsById: Object.keys(this.fieldsById).reduce(
        (acc, key) => {
          acc[key] = {
            ...this.fieldsById[key].toJSON()
          };

          return acc;
        },
        {} as Record<string, FieldJSON>
      ),
      views: this.views,
      viewsById: Object.keys(this.viewsById).reduce(
        (acc, key) => {
          acc[key] = {
            ...this.viewsById[key].toJSON()
          };

          return acc;
        },
        {} as Record<string, DatabaseViewJSON>
      ),
      ...(Object.keys(viewOrders).length > 0 && { viewOrders }),
      defaultViewId: this.defaultViewId,
      entries: this.entries,
      entriesById: Object.keys(this.entriesById).reduce((acc, key) => {
        acc[key] = {
          ...this.entriesById[key].toJSON()
        };

        return acc;
      }, {} as any),
      commentThreads: Object.keys(this.#commentThreads.data).reduce((acc, key) => {
        acc[key] = this.#commentThreads.data[key].toJSON();

        return acc;
      }, {} as any),
      defaultEntry: this.defaultEntry.toJSON(),
      isLocked: this.isLocked
    };
  }

  mutate(
    update: (database: MutableDatabase) => void,
    origin: CanvasTransactionOrigin = CanvasTransactionOrigin.LocalDefault
  ): void {
    this.doc.transact(() => {
      update(this as MutableDatabase);
    }, origin);
  }

  /**
   * Collects changed IDs without spreading them into function arguments:
   * persisted updates can contain more keys than the engine's argument limit.
   */
  getDiff = (transaction: Y.Transaction): DatabaseDiff => {
    if (transaction.doc !== this.doc) {
      throw new Error('cannot apply JSON diff from different doc');
    }

    let fieldsOrderingChanged = false;
    const changedFieldIds: string[] = [];

    let entriesOrderingChanged = false;
    const changedEntryIds: string[] = [];
    let threadsChanged = false;

    let viewsChanged = false;
    let defaultEntryChanged = false;
    let othersChanged = false;

    for (const changedYjsValue of transaction.changed.keys()) {
      let parent: Y.AbstractType<any> | null = changedYjsValue;
      while (parent) {
        if (parent === this.#entries.unwrap()) {
          entriesOrderingChanged = true;
          break;
        } else if (parent === this.#fields.unwrap()) {
          fieldsOrderingChanged = true;
          break;
        } else if (parent === this.#entriesById.unwrap()) {
          for (const id of transaction.changed.get(changedYjsValue) ?? []) {
            if (typeof id === 'string') {
              changedEntryIds.push(id);
            }
          }
          break;
        } else if (parent === this.#fieldsById.unwrap()) {
          for (const id of transaction.changed.get(changedYjsValue) ?? []) {
            if (typeof id === 'string') {
              changedFieldIds.push(id);
            }
          }
          break;
        } else if (parent.parent === this.#entriesById.unwrap()) {
          let entryId = null;
          (this.#entriesById.unwrap() as Y.Map<any>).forEach((val, key) => {
            if (parent && val === parent) {
              entryId = key;
            }
          });

          if (entryId) {
            changedEntryIds.push(entryId);
          }

          break;
        } else if (parent.parent === this.#fieldsById.unwrap()) {
          let fieldId = null;
          (this.#fieldsById.unwrap() as Y.Map<any>).forEach((val, key) => {
            if (parent && val === parent) {
              fieldId = key;
            }
          });

          if (fieldId) {
            changedFieldIds.push(fieldId);
          }
          break;
        } else if (
          parent === this.#views.unwrap() ||
          parent === this.#viewsById.unwrap() ||
          parent === this.#viewOrders.unwrap()
        ) {
          viewsChanged = true;
          break;
        } else if (parent === this.#commentThreads.unwrap()) {
          threadsChanged = true;
        } else if (parent === this.#settings.unwrap()) {
          othersChanged = true;
        } else if (parent === this.#defaultEntry.unwrap()) {
          defaultEntryChanged = true;
        }

        parent = parent.parent;
      }
    }

    return {
      fieldsOrderingChanged,
      entriesOrderingChanged,
      changedFieldIds,
      changedEntryIds: [...new Set(changedEntryIds)],
      viewsChanged,
      defaultEntryChanged,
      othersChanged,
      threadsChanged
    };
  };

  reset() {
    this.doc.transact(() => {
      this.doc.getArray(Properties.fields).delete(0, this.#fields.length);
      for (const fieldId of this.#fields.toArray()) {
        this.doc.getMap(Properties.fieldsById).delete(fieldId);
      }

      this.doc.getArray(Properties.views).delete(0, this.#views.length);

      for (const viewId of this.#views.toArray()) {
        this.doc.getMap(Properties.viewsById).delete(viewId);
      }
      this.#viewOrders.clear();

      Object.values(SettingsProperties).forEach((property) =>
        this.doc.getMap(Properties.settings).delete(property)
      );

      this.doc.getArray(Properties.entries).delete(0, this.#entries.length);
      for (const entryId of this.#entries.toArray()) {
        this.doc.getMap(Properties.entriesById).delete(entryId);
      }

      this.doc.getMap(Properties.commentThreads).clear();

      this.#defaultEntry.reset();
    });
  }

  merge(newDatabase: DatabaseJSON): void {
    this.doc.transact(() => {
      mergeIntoYdoc(this.doc, newDatabase, this.defaultValueConverters);
    });
  }

  replace(newDatabase: DatabaseJSON): void {
    this.doc.transact(() => {
      this.reset();
      this.merge(newDatabase);
    });

    activityLogger.log('yJSReplace', { ...newDatabase });
  }

  static fromJSON(
    json: DatabaseJSON,
    defaultValueConverters: DefaultValueConverters
  ): YDatabase {
    const doc = new Y.Doc();

    mergeIntoYdoc(doc, json, defaultValueConverters);

    return new YDatabase(doc, defaultValueConverters);
  }
}

const mergeIntoYdoc = (
  doc: Y.Doc,
  json: DatabaseJSON,
  defaultValueConverters: DefaultValueConverters
) => {
  doc.getArray(Properties.fields).push(json.fields);
  for (const fieldId of Object.keys(json.fieldsById)) {
    const field = YField.fromJSON(json.fieldsById[fieldId]);
    doc.getMap(Properties.fieldsById).set(fieldId, field.unwrap());
    field.dispose();
  }

  doc.getArray(Properties.views).push(json.views);
  for (const viewId of Object.keys(json.viewsById)) {
    const view = YView.fromJSON(json.viewsById[viewId], defaultValueConverters);
    doc.getMap(Properties.viewsById).set(viewId, view.unwrap());
    view.dispose();
  }
  new YViewOrders(doc.getMap(Properties.viewOrders)).merge(json.viewOrders ?? {});

  doc
    .getMap(Properties.settings)
    .set(SettingsProperties.defaultViewId, json.defaultViewId);
  doc.getMap(Properties.settings).set(SettingsProperties.isLocked, json.isLocked);

  doc.getArray(Properties.entries).push(json.entries);
  for (const entryId of Object.keys(json.entriesById)) {
    const entry = YEntry.fromJSON(
      json.entriesById[entryId],
      false,
      defaultValueConverters
    );
    doc.getMap(Properties.entriesById).set(entryId, entry.unwrap());
    entry.dispose();
  }

  // integrate default entry
  const entry = new YEntry(
    doc.getMap(Properties.defaultEntry),
    true,
    defaultValueConverters
  );
  for (const fieldId of Object.keys(json.defaultEntry.values)) {
    const val = entry.setValue(fieldId, json.defaultEntry.values[fieldId].type);
    setValueValue(val, json.defaultEntry.values[fieldId]);
  }
};
